/**
 * Define — the workspace's first section (stage `initial`).
 *
 * Carries the original client input (so the analyst defines against the
 * source, not from memory) and the request-shaping form: team, subject
 * lines, config. Config availability follows the *selected* team
 * (changing team reloads options without a Save). Commercials (TAT,
 * price, due date, expedited) live in the Client & commercials rail —
 * picking a config here announces it via `onConfigCommercials` so the
 * rail can pull the config's defaults into its form.
 *
 * The gate strip owns the ONLY submit ("Save & continue to Subjects") —
 * this section registers its save routine via `registerSubmit` so the
 * strip's primary persists the latest edits before advancing. There is
 * deliberately no standalone Save: a button that persists without
 * visibly moving anything reads as broken.
 *
 * Once the request has moved past `initial` the form is read-only —
 * the backend rejects out-of-stage edits, so the inputs must not
 * pretend otherwise. "Change" on the collapsed summary (revert) is the
 * honest way back in.
 */

import { useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { IconArrowsSplit } from '@tabler/icons-react';

import { ClientInputCard } from '@/components/ClientInputCard';
import { LinedTextarea } from '@/components/LinedTextarea';
import {
  useReportConfigs,
  useReportRequestAttachments,
  useTeams,
  type useReportRequestTriage,
} from '@/hooks';
import type { ReportConfigSummary } from '@/types/report-config';
import { ReportRequestDetail, TriageInitialUpdateRequest } from '@/types/report-request';

/**
 * Which attached files feed subject extraction. Checked = the resolved
 * effective set (explicit pick when the analyst made one, else the
 * provenance-scoped automatic set); any toggle persists the full
 * explicit list, and "Reset to automatic" clears it back to null.
 */
function ExtractionSourcesPicker({
  detail,
  updateExtractionSources,
  readOnly,
}: Readonly<{
  detail: ReportRequestDetail;
  updateExtractionSources: (attachmentIds: string[] | null) => Promise<unknown>;
  readOnly: boolean;
}>) {
  const { attachments } = useReportRequestAttachments(detail.id);
  const [saving, setSaving] = useState(false);
  if (attachments.length === 0) {
    return null;
  }

  const effective = new Set(detail.effectiveExtractionAttachmentIds);
  const explicit = detail.extractionSourceIds !== null;

  const save = async (next: string[] | null) => {
    setSaving(true);
    try {
      await updateExtractionSources(next);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (fileId: string) => {
    const next = new Set(effective);
    if (next.has(fileId)) {
      next.delete(fileId);
    } else {
      next.add(fileId);
    }
    void save([...next]);
  };

  return (
    <Stack gap={6} data-testid="extraction-sources">
      <Group gap="xs">
        <Text size="sm" fw={500}>
          Extraction sources
        </Text>
        {explicit && !readOnly && (
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={() => void save(null)}
            disabled={saving}
            data-testid="extraction-sources-auto"
          >
            Reset to automatic
          </Button>
        )}
      </Group>
      <Text size="xs" c="dimmed">
        Ticked files are read for subjects at extraction; other documents stay attached but are not
        read.
      </Text>
      {attachments.map((att) => (
        <Checkbox
          key={att.conversationAttachmentId}
          size="xs"
          label={att.filename ?? att.conversationAttachmentId}
          checked={effective.has(att.conversationAttachmentId)}
          disabled={readOnly || saving}
          onChange={() => toggle(att.conversationAttachmentId)}
          data-testid={`extraction-source-${att.conversationAttachmentId}`}
        />
      ))}
    </Stack>
  );
}

interface DefineSectionProps {
  detail: ReportRequestDetail;
  updateInitial: ReturnType<typeof useReportRequestTriage>['updateInitial'];
  /** Persists the explicit extraction file pick; the picker only
   * renders when the host wires it (the triage workspace does). */
  updateExtractionSources?: (attachmentIds: string[] | null) => Promise<unknown>;
  /** Hands the page this section's save routine — the gate strip's
   * primary calls it before advancing. Registered with the latest form
   * state. */
  registerSubmit: (submit: (() => Promise<void>) | null) => void;
  /** True when the request is past `initial` — render the fields
   * disabled instead of letting a save 409. */
  readOnly?: boolean;
  /** Announces a config pick so the Client & commercials rail can pull
   * the config's default TAT/price into its form. */
  onConfigCommercials?: (config: ReportConfigSummary | null) => void;
  /** Splits this request into one sibling per subject line (the fix for
   * extraction wrongly grouping single-subject reports). Save runs
   * first so the split sees the latest lines. */
  onSplit?: () => Promise<void>;
}

export function DefineSection({
  detail,
  updateInitial,
  updateExtractionSources,
  registerSubmit,
  readOnly = false,
  onSplit,
  onConfigCommercials,
}: Readonly<DefineSectionProps>) {
  const { teams } = useTeams();

  const [team, setTeam] = useState<string | null>(detail.teamId);
  const [reportConfigId, setReportConfigId] = useState<string | null>(detail.reportConfigId);
  // Config availability follows the *selected* team, not the persisted one.
  const { configs } = useReportConfigs({ teamId: team ?? undefined, isActive: true });

  const handleTeamChange = (next: string | null) => {
    setTeam(next);
    // A config chosen under the old team may not exist for the new one.
    if (next !== team) {
      setReportConfigId(null);
    }
  };

  const [subjectStrings, setSubjectStrings] = useState<string>(
    (detail.subjectStrings ?? []).join('\n')
  );

  const handleConfigChange = (next: string | null) => {
    setReportConfigId(next);
    // Commercials live in the Client & commercials rail — announce the
    // pick so the rail pulls the config's default TAT/price into its
    // form. Clearing the config leaves the rail alone (its values may
    // be hand-negotiated).
    if (next !== null && next !== reportConfigId) {
      onConfigCommercials?.(configs.find((c) => c.id === next) ?? null);
    }
  };

  // Re-sync from the server after a revert lands the request back at
  // `initial` (the read-only view may have gone stale in between).
  useEffect(() => {
    if (readOnly) {
      setTeam(detail.teamId);
      setReportConfigId(detail.reportConfigId);
      setSubjectStrings((detail.subjectStrings ?? []).join('\n'));
    }
  }, [readOnly, detail]);

  const teamSelectData = useMemo(
    () => teams.map((t) => ({ value: String(t.id), label: t.name })),
    [teams]
  );

  // Commercials (TAT / price / expedited) are deliberately ABSENT: the
  // rail owns them, and omitted fields are preserved server-side —
  // sending them here would clobber rail-saved values on advance.
  const buildPayload = (): TriageInitialUpdateRequest => ({
    team_id: team,
    report_config_id: reportConfigId,
    subject_strings: subjectStrings
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean),
  });

  const subjectLineCount = subjectStrings.split(/\r?\n/).filter((s) => s.trim()).length;
  const [splitConfirmOpen, setSplitConfirmOpen] = useState(false);
  const [splitting, setSplitting] = useState(false);
  const openSplitConfirm = () => setSplitConfirmOpen(true);
  const handleSplit = async () => {
    if (!onSplit) {
      return;
    }
    setSplitting(true);
    try {
      // Persist the latest lines first — the split reads what's saved.
      await updateInitial(buildPayload());
      await onSplit();
      setSplitConfirmOpen(false);
    } catch {
      // Errors are toasted by the hooks; keep the modal open.
    } finally {
      setSplitting(false);
    }
  };

  // Register the strip's primary routine, refreshed whenever form state
  // moves so the strip always saves the latest edits before advancing.
  useEffect(() => {
    if (readOnly) {
      registerSubmit(null);
      return;
    }
    registerSubmit(async () => {
      await updateInitial(buildPayload());
    });
    return () => registerSubmit(null);
  }, [readOnly, team, reportConfigId, subjectStrings, updateInitial, registerSubmit]);

  return (
    <Stack gap="sm">
      <ClientInputCard detail={detail} />

      {readOnly && (
        <Text size="xs" c="dimmed">
          These fields are read-only past Define — use “Change” on this section to revert and edit.
        </Text>
      )}

      <Select
        label="Team"
        placeholder="Select a team"
        data={teamSelectData}
        value={team}
        onChange={handleTeamChange}
        searchable
        clearable
        disabled={readOnly}
      />

      <LinedTextarea
        label="Subject lines"
        description="One per line. Each becomes one Subject after extraction."
        minRows={4}
        autosize
        value={subjectStrings}
        onChange={(e) => setSubjectStrings(e.currentTarget.value)}
        disabled={readOnly}
      />
      {!readOnly && onSplit && subjectLineCount > 1 && (
        <Group justify="flex-end" mt={-6}>
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconArrowsSplit size={14} />}
            onClick={openSplitConfirm}
            data-testid="define-split-request"
          >
            Split into {subjectLineCount} separate requests
          </Button>
        </Group>
      )}

      {updateExtractionSources && (
        <ExtractionSourcesPicker
          detail={detail}
          updateExtractionSources={updateExtractionSources}
          readOnly={readOnly}
        />
      )}

      <Select
        label="Report config"
        placeholder="Select a config"
        // Admin surface — mark manual/Backoffice-docx flows inline.
        data={configs.map((c) => ({
          value: c.id,
          label: c.legacyFlow ? `${c.name} · legacy` : c.name,
        }))}
        value={reportConfigId}
        onChange={handleConfigChange}
        searchable
        clearable
        disabled={readOnly}
      />

      <Modal
        opened={splitConfirmOpen}
        onClose={() => setSplitConfirmOpen(false)}
        title="Split into separate requests?"
      >
        <Stack gap="sm">
          <Text size="sm">
            Each subject line becomes its own report request with the same team, config, pricing and
            attachments. This request keeps the first line; {subjectLineCount - 1} new{' '}
            {subjectLineCount - 1 === 1 ? 'request is' : 'requests are'} created.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setSplitConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={splitting}
              onClick={() => void handleSplit()}
              data-testid="confirm-split"
            >
              Split
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
